import { ContextConsumer } from '@lit/context'
import clsx from 'clsx'
import { html, nothing, unsafeCSS } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { t } from '@/localization/localization'

import { CodePuncherContext, codePuncherContext } from './code-puncher.context'
import styles from './code-puncher-slot.scss?inline'

/**
 * `dap-ds-code-puncher-slot`
 * @summary Individual digit input slot for code puncher.
 * @element dap-ds-code-puncher-slot
 * @title - Code puncher slot
 *
 * @csspart base - The slot container.
 * @csspart input - The input element.
 */
@customElement('dap-ds-code-puncher-slot')
export default class DapDSCodePuncherSlot extends DdsElement {
  static readonly styles = unsafeCSS(styles)

  // Context consumer - kept alive to maintain subscription
  // @ts-expect-error - Consumer is needed to maintain subscription, but not directly accessed
  private _consumer!: ContextConsumer<typeof codePuncherContext, this>
  private _ctx: CodePuncherContext | undefined

  @query('#input') private _input!: HTMLInputElement

  @state() private _index = -1
  @state() private _isActive = false
  @state() private _registered = false

  connectedCallback(): void {
    super.connectedCallback()
    // Create context consumer
    this._consumer = new ContextConsumer(this, {
      context: codePuncherContext,
      subscribe: true,
      callback: ctx => {
        this._ctx = ctx
        // Register slot when context first becomes available
        if (ctx && !this._registered) {
          this._index = ctx.registerSlot(this)
          this._registered = true
        }
        this.requestUpdate()
      },
    })
  }

  disconnectedCallback(): void {
    if (this._ctx && this._registered) {
      this._ctx.unregisterSlot(this)
      this._registered = false
    }
    super.disconnectedCallback()
  }

  protected willUpdate(_changedProperties: Map<string, unknown>): void {
    super.willUpdate(_changedProperties)
    // Update active state from context
    if (this._ctx) {
      this._isActive = this._ctx.activeIndex === this._index
    }
  }

  // Get properties from context
  get disabled(): boolean {
    return this._ctx?.disabled ?? false
  }

  get readonly(): boolean {
    return this._ctx?.readonly ?? false
  }

  get size(): 'xs' | 'sm' | 'lg' {
    return (this._ctx?.size as 'xs' | 'sm' | 'lg') ?? 'sm'
  }

  // Focus the internal input
  focus(): void {
    this._input?.focus()
  }

  // Input handler to validate and notify parent
  private _onInput = (): void => {
    if (!this._input || !this._ctx) return

    const char = this._input.value

    if (char === '') {
      this._ctx.setCharAt(this._index, '')
    } else if (this._ctx.pattern.test(char)) {
      this._ctx.setCharAt(this._index, char)
      // Auto-advance to next slot
      if (this._index < this._ctx.slotCount - 1) {
        this._ctx.focusSlot(this._index + 1)
      }
    } else {
      // Reject invalid character, restore previous value
      this._input.value = this._ctx.getCharAt(this._index)
    }
  }

  // Keydown handler for navigation
  private _onKeydown = (event: KeyboardEvent): void => {
    if (!this._ctx) return
    this._ctx.handleKeydown(this._index, event)
  }

  // Handle paste at slot level
  private _handlePaste = (event: ClipboardEvent): void => {
    if (!this._ctx) return
    this._ctx.handlePaste(event)
  }

  // Focus handler
  private _onFocus = (): void => {
    if (!this._ctx) return
    this._isActive = true
    // Select existing content so typing replaces it
    if (this._input?.value) {
      this._input.select()
    }
    // Notify parent of focus
    const parent = this.closest('dap-ds-code-puncher') as HTMLElement & {
      setActiveIndex: (i: number) => void
    }
    parent?.setActiveIndex(this._index)
    this.requestUpdate()
  }

  // Blur handler
  private _onBlur = (): void => {
    this._isActive = false
    this.requestUpdate()
  }

  render() {
    if (!this._ctx || this._index < 0) {
      return nothing
    }

    const char = this._ctx.getCharAt(this._index)
    const showPlaceholder = !char && this._ctx.placeholder && !this._isActive
    const slotCount = this._ctx.slotCount

    return html`
      <div
        part="base"
        class=${clsx('code-puncher-slot', `code-puncher-slot--${this.size}`, {
          'code-puncher-slot--filled': !!char,
        })}>
        <input
          id="input"
          part="input"
          class=${clsx(
            'code-puncher-slot__input',
            `code-puncher-slot__input--${this.size}`,
            {
              'code-puncher-slot__input--disabled': this.disabled,
              'code-puncher-slot__input--readonly': this.readonly,
              'code-puncher-slot__input--success':
                this._ctx.status === 'success',
              'code-puncher-slot__input--error': this._ctx.status === 'error',
            },
          )}
          type=${this._ctx.mask ? 'password' : 'text'}
          inputmode="numeric"
          maxlength="1"
          .value=${char}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          autocomplete=${(this._index === 0
            ? this._ctx.autocomplete
            : 'off') as any}
          aria-label=${t('code-puncher.slot.aria-label', {
            index: this._index + 1,
            count: slotCount,
          })}
          @input=${this._onInput}
          @keydown=${this._onKeydown}
          @focus=${this._onFocus}
          @blur=${this._onBlur}
          @paste=${this._handlePaste} />
        ${showPlaceholder
          ? html`<span class="code-puncher-slot__placeholder"
              >${this._ctx.placeholder}</span
            >`
          : nothing}
        ${this._isActive && !char
          ? html`<span class="code-puncher-slot__caret"></span>`
          : nothing}
      </div>
    `
  }
}
